/**
 * In All businesses mode, a page that works on one business shows its own
 * business bar and sends that business with its requests — the owner moves
 * between Money, Insights, People and Admin without picking a business
 * for the whole app. Outside All businesses mode this renders children only.
 */
import { lazy, Suspense, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Building2, Layers, Loader2 } from "lucide-react";
import useAuthStore from "@/stores/authStore";
import useBusinessStore from "@/stores/businessStore";
import { useMyBusinesses } from "@/hooks/useBusinesses";
import { groupScopeFor, togetherViewFor, type TogetherView } from "@/constants/group-mode";
import { cn } from "@/lib/utils";

const LAST_KEY = "eaze.pageBusiness";
const readLast = () => {
  try {
    return sessionStorage.getItem(LAST_KEY);
  } catch {
    return null;
  }
};
const writeLast = (id: string) => {
  try {
    sessionStorage.setItem(LAST_KEY, id);
  } catch {
    /* not remembered — fine */
  }
};

const GroupExpensesTogether = lazy(() => import("./GroupMoneyTogether").then((m) => ({ default: m.GroupExpensesTogether })));
const GroupPayrollTogether = lazy(() => import("./GroupMoneyTogether").then((m) => ({ default: m.GroupPayrollTogether })));

// "All businesses" is the first view of a money page; a business chip
// switches to that one business (remembered for this visit).
const TOGETHER_KEY = (v: TogetherView) => `eaze.together.${v}`;
const readTogether = (v: TogetherView) => {
  try {
    return sessionStorage.getItem(TOGETHER_KEY(v)) !== "0";
  } catch {
    return true;
  }
};
const writeTogether = (v: TogetherView, on: boolean) => {
  try {
    sessionStorage.setItem(TOGETHER_KEY(v), on ? "1" : "0");
  } catch {
    /* fine */
  }
};

type Biz = { _id: string; name: string; slug: string; isDefault?: boolean };

export default function PageBusinessScope({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const role = useAuthStore((s) => s.user?.role);
  const slug = useBusinessStore((s) => s.activeBusinessSlug);
  const pageBusinessId = useBusinessStore((s) => s.pageBusinessId);
  const setPage = useBusinessStore((s) => s.setPageBusinessId);
  const tenantWide = role === "tenant_owner" || role === "super_admin";
  const groupMode = tenantWide && !slug;
  const scope = groupMode ? groupScopeFor(pathname) : null;
  const { data } = useMyBusinesses();
  const businesses = useMemo(() => (Array.isArray(data) ? (data as Biz[]) : []), [data]);
  const view = scope === "one" && businesses.length > 1 ? togetherViewFor(pathname) : null;
  const [togetherOn, setTogetherOn] = useState<Partial<Record<TogetherView, boolean>>>({});
  const together = view ? (togetherOn[view] ?? readTogether(view)) : false;
  const showTogether = (on: boolean) => {
    if (!view) return;
    writeTogether(view, on);
    setTogetherOn((s) => ({ ...s, [view]: on }));
  };

  // Pick the page's business before the page asks for anything.
  const wanted = useMemo(() => {
    if (scope !== "one" || !businesses.length) return null;
    const keep = businesses.find((b) => b._id === pageBusinessId) || businesses.find((b) => b._id === readLast());
    return (keep || businesses.find((b) => b.isDefault) || businesses[0])._id;
  }, [scope, businesses, pageBusinessId]);

  useLayoutEffect(() => {
    if (scope === "one" && wanted && wanted !== pageBusinessId) setPage(wanted);
    if (scope !== "one" && pageBusinessId) setPage(null);
  }, [scope, wanted, pageBusinessId, setPage]);

  useEffect(() => () => setPage(null), [setPage]);

  if (scope !== "one") return <>{children}</>;
  if (!wanted || pageBusinessId !== wanted) return null; // a moment, while the business is set

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card px-3 py-2" role="group" aria-label="Business shown on this page">
        <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Building2 className="h-3.5 w-3.5" aria-hidden /> Showing
        </span>
        <div className="flex flex-wrap gap-1.5">
          {view && (
            <button
              type="button"
              aria-pressed={together}
              onClick={() => showTogether(true)}
              className={cn(
                "inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                together ? "border-[#4CAF50] bg-[#4CAF50]/10 font-medium text-foreground" : "border-border text-muted-foreground hover:bg-muted",
              )}
            >
              <Layers className="h-3.5 w-3.5" aria-hidden /> All businesses
            </button>
          )}
          {businesses.map((b) => (
            <button
              key={b._id}
              type="button"
              aria-pressed={!together && b._id === pageBusinessId}
              onClick={() => {
                writeLast(b._id);
                setPage(b._id);
                showTogether(false);
              }}
              className={cn(
                "rounded-full border px-3 py-1 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                !together && b._id === pageBusinessId ? "border-[#4CAF50] bg-[#4CAF50]/10 font-medium text-foreground" : "border-border text-muted-foreground hover:bg-muted",
              )}
            >
              {b.name}
            </button>
          ))}
        </div>
        {pathname.startsWith("/reports") && (
          <Link to="/group" className="ml-auto text-xs font-medium text-[#2E7D32] hover:underline">
            All businesses together →
          </Link>
        )}
      </div>
      {together && view ? (
        <Suspense fallback={<Loader2 className="mx-auto my-12 h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading" />}>
          {view === "expenses" ? <GroupExpensesTogether /> : <GroupPayrollTogether />}
        </Suspense>
      ) : (
        <div key={pageBusinessId}>{children}</div>
      )}
    </>
  );
}
